import {
  Box,
  Button,
  Flex,
  Heading,
  IconButton,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  VStack,
} from "@chakra-ui/react";
import TagModal from "../Tags/TagDrawer";
import React, { useContext, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { BiConversation, BiMessageSquareDetail } from "react-icons/bi";
import { HiOutlineCpuChip } from "react-icons/hi2";
import { IoDocumentTextOutline } from "react-icons/io5";
import { MdOutlineSettingsVoice } from "react-icons/md";
import { PiBookOpenTextLight } from "react-icons/pi";
import { TiChartBarOutline } from "react-icons/ti";
import { VscTools } from "react-icons/vsc";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { fetchController } from "../../utils/FetchController/fetchController";
import { getHostDomain } from "../../utils/utils";
import { AgencyContext } from "../Settings/AgencyProvider/AgencyProvider";
import { SettingContext } from "../Settings/Reducer/SettingProvider";
import AllowHumanHandover from "./AllowHumanHandover";
import AssistantType from "./AssistantType";
import BasicDetails from "./BasicDetails";
import ConnectTools from "./ConnectTools";
import ConversationFields from "./ConversationFields";
import DataSourcesField from "./DataSourcesField";
import FormFields from "./FormFields";
import LLMrelated from "./LLMrelated";
import Prompt from "./Prompt";
import VoicesField from "./VoicesField";
import WebhookAndIntentFields from "./WebhookAndIntentFields";
import { IoMdArrowRoundBack } from "react-icons/io";
import useTagSelect from "../Tags/useTagSelect";
import { GrDeploy } from "react-icons/gr";
import NewDeployemnt from "./NewDeployemnt";
import AssistantPreviewPanel from "./AssistantPreviewPanel";

const AddEditAssistant_new = () => {
  const agency = useContext(AgencyContext);
  const { textColor, buttonColorScheme, myDetails } = agency;
  const assistantFormMethod = useForm({
    defaultValues: {
      assistant_type: "simple",
      templatePhonePrompt: undefined,
      voice: false,
      hide_ds: false,
      has_human_agent: false,
      use_tools: false,
      voiceLanguages: [],
      llmModel: null,
      systemPrompt: "",
      attributes: {
        conversation: false,
        contact: false,
        first_sentence: "",
        interruption_time: 10,
      },
      conversation_flow_id: null,
    },
  });
  const { handleSubmit, watch, setValue, reset, getValues, formState: { isDirty } } =
    assistantFormMethod;
  const [saveCount, setSaveCount] = useState(0);
  // Stores values to reset() after the form mounts (post-loading)
  const [pendingReset, setPendingReset] = useState(null);

  const baseUrl = getHostDomain();
  const { assistantId } = useParams();
  const navigate = useNavigate();

  const [myownVoiceOptions, setMyownVoiceOptions] = useState([]);
  const [editData, setEditData] = useState(null);
  const [member, setMember] = useState(false);
  const { state } = useContext(SettingContext);

  const [promptType, setPromptType] = useState("");
  const [status, setStatus] = useState("idle");
  const [linkAssistantTools, setLinkAssistantTools] = useState([]);
  const loading = status === "loading";
  const resetForm = () =>
    reset({
      assistant_type: "simple",
      voice: false,
      llmModel: null,
      systemPrompt: "",
      description: "",
      name: "",
      has_human_agent: false,
      use_tools: false,
      voiceLanguages: [],
      attributes: {
        conversation: false,
        contact: false,
      },
      conversation_flow_id: null,
    });

  const onSubmitAssistantPhone = async (data, exit = true) => {
    try {
      setStatus("loading");
      if (
        !data.custom_voice &&
        data.voiceLanguages.some((lang) => !lang.speaker)
      ) {
        toast.error("Please attach a speaker to the language");
        return;
      }

      const phoneRes = await fetchController(
        baseUrl +
          (data.id ? `/api/v1/assistant/${data.id}` : "/api/v1/assistant"),
        data.id ? "PUT" : "POST",
        {
          assistant_type: data.assistant_type || "simple",
          llm_model: data.llmModel?.value || "gpt-3.5-turbo-1106",
          name: data.name,
          voice: data.voice,
          system_prompt: data.systemPrompt,
          description: data.description || "string",
          use_tools: watch("use_tools"),
          voice_languages:
            data.voiceLanguages?.map((lang) => ({
              stt: lang.id,
              tts: lang.speaker.value,
            })) || [],
          custom_voice: data.custom_voice,
          attributes: data.attributes || {},
          has_human_agent: data?.has_human_agent || false,
          hide_ds: data?.hide_ds || false,
          show_images: data.showImages || false,
          conversation_flow_id: data?.conversation_flow_id || null,
        }
      );
      toast.success(`Assistant ${data.id ? "updated" : "created"}`);
      if (data.id) { setSaveCount((c) => c + 1); reset(getValues()); }
      if (exit) navigate("/ai-agents/assistants");
      else if (!data.id && phoneRes?.data?.id) navigate(`/ai-agents/assistants/${phoneRes.data.id}`);
    } catch (error) {
      toast.error("Some error occured");
    } finally {
      setStatus("idle");
    }
  };

  const onSubmit = async (data, exit = true) => {
    try {
      setStatus("loading");

      const endpoint = data.id
        ? `/api/v1/assistant/${data.id}`
        : "/api/v1/assistant";
      const urlHit = baseUrl + endpoint;

      if (data.voice) {
        if (data.voiceLanguages.some((lang) => !lang.speaker)) {
          toast.error("Please attach a speaker to the language");
          return;
        }
      }

      const attributes = data.attributes || {};
      // detect_voicemail is now a string ("hangup" | "drop" | null) — UI sets it directly.
      // Coerce legacy boolean values (true → "hangup") for backwards compatibility.
      if (attributes?.detect_voicemail === true) {
        attributes["detect_voicemail"] = "hangup";
      } else if (attributes?.detect_voicemail === false || attributes?.detect_voicemail === "") {
        attributes["detect_voicemail"] = null;
      }
      if (attributes?.conversation_timeout) {
        attributes.conversation_timeout = attributes.conversation_timeout * 60;
      }
      const body = {
        assistant_type: data.assistant_type || "simple",
        llm_model: data.llmModel?.value || "gpt-3.5-turbo-1106",
        name: data.name || "string",
        description: data.description || "string",
        use_tools: watch("use_tools"),
        attributes,
        system_prompt: data.systemPrompt,
        voice: data.voice,
        voice_languages:
          data.voiceLanguages?.map((lang) => ({
            stt: lang.id,
            tts: lang.speaker && lang.speaker.value,
          })) || [],
        custom_voice: data.custom_voice,
        show_images: data.showImages,
        hide_ds: data.hide_ds || false,
        has_human_agent: data.has_human_agent,
        conversation_flow_id: data?.conversation_flow_id || null,
      };
      const res = await fetchController(urlHit, data.id ? "PUT" : "POST", body);
      toast.success(
        data.id ? "Assistant Updated" : "Assistant Added",
        "success"
      );
      if (data.id) { setSaveCount((c) => c + 1); reset(getValues()); }
      if (exit) navigate("/ai-agents/assistants");
      else if (!data.id && res?.data?.id) navigate(`/ai-agents/assistants/${res.data.id}`);
    } catch (error) {
      toast.error("Some error occured");
    } finally {
      setStatus("idle");
    }
  };

  const loadOwnVoice = async () => {
    const endpoint = `/api/v1/voice/list`;
    const urlHit = baseUrl + endpoint;
    const getData = await fetchController(urlHit, "GET");

    const modifiedData = getData?.data?.items?.map((val, index) => {
      return {
        value: val.id,
        label: val.name,
      };
    });

    setMyownVoiceOptions(modifiedData);
  };
  useEffect(() => {
    loadOwnVoice();
  }, []);

  const [voiceSsts, setVoiceSsts] = useState([]);
  const [voices, setVoices] = useState([]);
  const languages = voiceSsts.map((language) => ({
    label: language.name,
    value: language.locale_code,
    id: language.id,
    language_group: language.language_group,
    voicestt_provider: language.voicestt_provider,
  }));

  const getAssistantTool = async () => {
    try {
      const response = await fetchController(
        baseUrl + `/api/v1/assistant/${assistantId}/LinkAssistantTool`,
        "GET"
      );
      setLinkAssistantTools(response?.data?.items || []);
      setValue("use_tools", response?.data?.items?.length > 0);
    } catch (e) {
      console.error(e);
    }
  };

  const getVoices = async (queries = {}) => {
    const { searchQuery, provider, language } = queries;
    let queryParams = [];

    if (searchQuery) queryParams.push(`search_term=${searchQuery}`);
    if (provider) queryParams.push(`provider=${provider}`);
    if (language) queryParams.push(`language=${language}`);

    const queryString = queryParams.length ? `&${queryParams.join("&")}` : "";

    const { data: voicesData } = await fetchController(
      baseUrl + `/api/v1/voice/list/voicetts?size=1000${queryString}`
    );
    setVoices(voicesData.items);

    return voicesData;
  };

  useEffect(() => {
    (async () => {
      setStatus("loadingAssitant");

      const response = await fetchController(
        baseUrl + "/api/v1/voice/list/voicestt?size=1000"
      );
      const voicesData = await getVoices();
      setVoiceSsts(response.data.items);
      setMember(state?.credits?.human_agents > 0 ? true : false);
      if (assistantId) {
        try {
          const { data } = await fetchController(
            baseUrl + `/api/v1/assistant/${assistantId}`
          );
          setEditData(data);
          await getAssistantTool();

          setValue("assistant_type", data.assistant_type);
          setValue("custom_voice", data.custom_voice);
          setValue("name", data.name);
          setValue("hide_ds", data.hide_ds || false);
          setValue("has_human_agent", data.has_human_agent || false);

          setValue("description", data.description);
          setValue("systemPrompt", data.system_prompt);

          setValue("voice", data.voice);
          const selectedStts = data.voice_languages.map(
            (voice_lang) => voice_lang.stt
          );

          const selectedVoiceLanguages = response.data.items
            .filter((lang) => selectedStts.includes(lang.id))
            .map((language) => ({
              label: language.name,
              value: language.locale_code,
              id: language.id,
              language_group: language.language_group,
              voicestt_provider: language.voicestt_provider,
            }));

          const voiceLanguages = selectedVoiceLanguages.map(
            (selectedVoiceLang, index) => {
              const speakerId = data.voice_languages[index].tts;
              const voicePerson = voicesData.items.find(
                (voice) => speakerId === voice.id
              );
              return {
                ...selectedVoiceLang,
                speaker: voicePerson && {
                  label: `${voicePerson.name} (${voicePerson.credits}) credits`,
                  value: speakerId,
                  voicetts_provider: voicePerson.voicetts_provider,
                },
              };
            }
          );

          setValue("voiceLanguages", voiceLanguages);
          setValue("showImages", data.show_images);
          setValue("id", data.id);
          setValue("assistant_type", data.assistant_type);
          setValue("attributes", {
            ...data.attributes,
            conversation_timeout: data.attributes?.conversation_timeout
              ? data.attributes?.conversation_timeout / 60
              : null,
          });
          setValue("conversation_flow_id", data?.conversation_flow_id || null);
          setPendingReset(getValues());
        } catch (error) {
          resetForm();
        } finally {
          setStatus("idle");
        }
      } else {
        const defaultVoiceLanguages = response.data.items
          .filter((item) => item.locale_code === "en")
          .map((language) => ({
            label: language.name,
            value: language.locale_code,
            id: language.id,
            language_group: language.language_group,
            voicestt_provider: language.voicestt_provider,
          }));
        setPendingReset({
          assistant_type: "simple",
          voice: false,
          llmModel: null,
          systemPrompt: "",
          description: "",
          name: "",
          has_human_agent: false,
          use_tools: false,
          voiceLanguages: defaultVoiceLanguages,
          attributes: { conversation: false, contact: false },
          conversation_flow_id: null,
        });
      }
      setStatus("idle");
    })();
  }, [assistantId, baseUrl]);

  const loadingAssitant = status === "loadingAssitant";

  // Call reset() AFTER the form has mounted (all register() calls done),
  // so isDirty is correctly false when the user first sees the form.
  useEffect(() => {
    if (!loadingAssitant && pendingReset) {
      reset(pendingReset);
      setPendingReset(null);
    }
  }, [loadingAssitant, pendingReset]);

  const handlePromptSave = () => {
    const text = getValues("systemPrompt");
    const systemPromptText = text.replace(/\s\/$/, "");
    setValue("systemPrompt", systemPromptText);
    return systemPromptText;
  };

  const onSaveAndExit = async () => {
    const submit =
      watch("assistant_type") === "simple"
        ? handleSubmit(onSubmit)
        : handleSubmit(onSubmitAssistantPhone);
    handlePromptSave();
    submit();
  };

  const onSave = () => {
    handleSubmit((data) => {
      const promptText = handlePromptSave();
      data.systemPrompt = promptText;
      if (data.assistant_type === "phone") {
        onSubmitAssistantPhone(data, false);
      } else {
        onSubmit(data, false);
      }
    })();
  };

  const toolTipMsg =
    myDetails && myDetails?.agency_id
      ? `Please contact ${myDetails?.branding?.agency_name || ""} `
      : "Please upgrade to Platinum or above";

  const assistantTabList = {
    Model: [
      {
        title: "LLM Related",
        icon: HiOutlineCpuChip,
        component: (
          <LLMrelated
            editData={editData}
            promptType={promptType}
            setPromptType={setPromptType}
          />
        ),
      },
      {
        title: "Prompt",
        icon: BiMessageSquareDetail,
        component: (
          <Prompt promptType={promptType} setPromptType={setPromptType} />
        ),
      },
    ],
    "Voice": [
      {
        title: " Voice",
        icon: MdOutlineSettingsVoice,
        component: (
          <VoicesField
            myownVoiceOptions={myownVoiceOptions}
            languages={languages}
            voices={voices}
            getVoices={getVoices}
          />
        ),
      },
    ],
    Tools: [
      {
        title: "Tools",
        icon: VscTools,
        component: (
          <ConnectTools
            assistantId={assistantId}
            getAssistantTool={getAssistantTool}
            linkAssistantTools={linkAssistantTools}
          />
        ),
      },
    ],
    "Data Sources": [
      {
        title: "Data Sources",
        icon: TiChartBarOutline,
        component: (
          <DataSourcesField member={member} assistantId={assistantId} />
        ),
      },
    ],
    Advanced: [
      {
        title: "AllowHumanHandover",
        component: (
          <AllowHumanHandover member={member} toolTipMsg={toolTipMsg} />
        ),
      },
      {
        title: "Form",
        icon: IoDocumentTextOutline,
        component: (
          <FormFields assistantId={assistantId} linkAssistantForms={[]} />
        ),
      },
      {
        title: "Conversation",
        icon: BiConversation,
        component: <ConversationFields member={member} />,
      },
      {
        title: "Webhook & intent",
        icon: PiBookOpenTextLight,
        component: (
          <WebhookAndIntentFields assistantId={assistantId} fetch={null} />
        ),
      },
    ],
    Deploy: [
      {
        title: "Deploy",
        icon: GrDeploy,
        component: <NewDeployemnt assistantData={editData} />,
      },
    ],
  };
  const tagSelectHook = useTagSelect({
    entity_id: watch("id"),
    entity_type: "assistant",
  });

  return (
    <>
    <FormProvider {...assistantFormMethod}>
        <form style={{ height: "100%", width:"100%" }}>
          {loadingAssitant ? (
            <Flex justifyContent={"center"} alignItems={"center"} h={"100%"}>
              <Spinner />
            </Flex>
          ) : (
            <Flex h="100%" gap={6} align="start" w="100%" px={2} py={2}>
              <Box flex="3" minW={0}>
            <VStack h="100%" width="100%" spacing={2} borderRadius="md">
              <Flex direction={"column"} gap={3} w={"100%"}>
                <Flex gap={2} w={"100%"} alignItems="center" justify="space-between" py={2}>
                  <Flex gap={2} alignItems="center">
                    <IconButton
                      icon={<IoMdArrowRoundBack />}
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(-1)}
                    />
                    <Heading color={textColor} fontSize="xl">
                      {watch("id") ? "Update Assistant" : "Add an Assistant"}
                    </Heading>
                  </Flex>
                  <Flex gap={2}>
                    <Button
                      type="button"
                      variant="outline"
                      colorScheme={buttonColorScheme}
                      isLoading={loading}
                      isDisabled={!isDirty || loading}
                      onClick={onSaveAndExit}
                    >
                      Save & Exit
                    </Button>
                    <Button
                      type="button"
                      isLoading={loading}
                      isDisabled={!isDirty || loading}
                      colorScheme={buttonColorScheme}
                      onClick={onSave}
                    >
                      Save
                    </Button>
                  </Flex>
                </Flex>

                <BasicDetails id={watch("id")} tagSelectHook={tagSelectHook} />
                <AssistantType voiceSsts={voiceSsts} voices={voices} />
              </Flex>

              <Box w={"100%"}>
                <Tabs>
                  <TabList>
                    {Object.keys(assistantTabList)
                      .filter(
                        (assistantTab) =>
                          !(
                            watch("assistant_type") === "realtime_openai" &&
                            assistantTab === "Data Sources"
                          ) && !(!assistantId && assistantTab === "Deploy")
                      )
                      .map((key) => {
                        return <Tab key={key}>{key}</Tab>;
                      })}
                  </TabList>

                  <TabPanels>
                    {Object.entries(assistantTabList)
                      .filter(
                        ([key]) =>
                          !(
                            watch("assistant_type") === "realtime_openai" &&
                            key === "Data Sources"
                          )
                      )
                      .map(([key, value]) => (
                        <TabPanel key={key}>
                          {value.map((tabPanel, index) => (
                            <React.Fragment key={index}>
                              {tabPanel.component}
                            </React.Fragment>
                          ))}
                        </TabPanel>
                      ))}
                  </TabPanels>
                </Tabs>
              </Box>
            </VStack>
              </Box>

              <Box flex="2" minW={0} position="sticky" top={0} pt={2}>
                <AssistantPreviewPanel
                  assistantId={assistantId}
                  assistantType={watch("assistant_type")}
                  assistantName={watch("name")}
                  saveCount={saveCount}
                />
              </Box>
            </Flex>
          )}
        </form>
      </FormProvider>
      {tagSelectHook.tagDrawerDisclosure?.isOpen && (
        <TagModal
          isOpen={tagSelectHook.tagDrawerDisclosure.isOpen}
          onClose={tagSelectHook.tagDrawerDisclosure.onClose}
          tagToEdit={tagSelectHook.tagToEdit}
          setTagToEdit={tagSelectHook.setTagToEdit}
          cb={tagSelectHook.onCreateTag}
        />
      )}
    </>
      
  );
};

export default AddEditAssistant_new;
